/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import NProgress from 'nprogress';

/** Delay before showing the progress bar to avoid flashing on fast API requests (ms) */
const PROGRESS_START_DELAY_MS = 150;

/** Auto-reset safety net — if a request hangs longer than this, force-hide the progress bar (ms) */
const PROGRESS_TIMEOUT_MS = 10_000;

let activeRequests = 0;
let navigating = false;
let startDelayTimer: ReturnType<typeof setTimeout> | null = null;
let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

const clearTimers = () => {
  if (startDelayTimer) {
    clearTimeout(startDelayTimer);
    startDelayTimer = null;
  }

  if (timeoutTimer) {
    clearTimeout(timeoutTimer);
    timeoutTimer = null;
  }
};

const isActive = () => activeRequests > 0 || navigating;

/** Keeps bar at 100% without fading out */
const completeProgress = () => {
  NProgress.set(1);
};

/** Resets NProgress state so the next start() begins from zero */
const restartProgress = () => {
  NProgress.status = null;
  NProgress.start();
};

export const progressTracker = {
  /** Called on pushState/replaceState — binary flag, not counted */
  startNavigation() {
    if (globalThis.window === undefined || navigating) {
      return;
    }

    navigating = true;
    clearTimers();
    restartProgress();

    timeoutTimer = setTimeout(() => {
      progressTracker.reset();
    }, PROGRESS_TIMEOUT_MS);
  },

  /** Called when page renders with new params */
  finishNavigation() {
    if (globalThis.window === undefined || !navigating) {
      return;
    }

    navigating = false;

    if (!isActive()) {
      clearTimers();
      completeProgress();
    }
  },

  /** Called by API middleware on request start */
  increment() {
    if (globalThis.window === undefined) {
      return;
    }

    activeRequests++;

    if (activeRequests === 1 && !navigating) {
      clearTimers();

      startDelayTimer = setTimeout(() => {
        if (activeRequests > 0) {
          restartProgress();
        }

        startDelayTimer = null;
      }, PROGRESS_START_DELAY_MS);

      timeoutTimer = setTimeout(() => {
        progressTracker.reset();
      }, PROGRESS_TIMEOUT_MS);
    }
  },

  /** Called by API middleware on response/error */
  decrement() {
    if (globalThis.window === undefined || activeRequests === 0) {
      return;
    }

    activeRequests--;

    if (!isActive()) {
      clearTimers();
      completeProgress();
    }
  },

  reset() {
    if (globalThis.window === undefined) {
      return;
    }

    activeRequests = 0;
    navigating = false;
    clearTimers();
    NProgress.done();
  },
};
